import { FC, useCallback, useState } from 'react'

import { EdgeLabelRenderer, EdgeProps, getSmoothStepPath } from '@xyflow/react'
import { AnimatePresence, motion } from 'framer-motion'
import { useAtomValue } from 'jotai'
import { useDebouncedCallback } from 'use-debounce'

import { GreyGrey40, IndigoIndigo50 } from '@sequencehq/core-components'

import { isEditingDisabledAtom } from 'Automations/automationEditor/atoms'
import { EDGE_INSERT_BUTTON_SIZE, EdgeInsertButton } from './EdgeInsertButton'

const ARROW_WIDTH = 7
const ARROW_HEIGHT = 14
const HANDLE_OFFSET = 4

export const DefaultEdge: FC<EdgeProps> = ({
  id,
  selected,
  data,
  ...props
}) => {
  const [path, labelX, labelY] = getSmoothStepPath({
    ...props,
    borderRadius: 20,
    targetX: props.targetX - HANDLE_OFFSET
  })
  const connectedToSelectedNode = Boolean(data?.connectedToSelectedNode)
  const isPreview = Boolean(data?.isPreview)
  const color =
    selected || connectedToSelectedNode ? IndigoIndigo50 : GreyGrey40
  const markerId = `arrow-${id}`

  const isEditingDisabled = useAtomValue(isEditingDisabledAtom)

  // Hovering the edge's hit-area path (below) reveals the "+"; keep it shown
  // while the picker is open even after the pointer leaves the edge.
  const [isInsertButtonVisible, setIsInsertButtonVisible] = useState(false)
  const [isInsertOpen, setIsInsertOpen] = useState(false)
  // Bridge the gap between leaving the SVG hit area and entering the
  // (portaled) insert button: debounce the hide so quick transitions
  // between the two don't flicker the button away.
  const hideInsertButton = useDebouncedCallback(
    () => setIsInsertButtonVisible(false),
    100
  )
  const showInsertButton = useCallback(() => {
    hideInsertButton.cancel()
    setIsInsertButtonVisible(true)
  }, [hideInsertButton])

  return (
    <>
      <g
        data-testid={`automations.editor.edge.${id}`}
        data-highlighted={connectedToSelectedNode}
      >
        <defs>
          <marker
            id={markerId}
            markerWidth={ARROW_WIDTH}
            markerHeight={ARROW_HEIGHT}
            refX={ARROW_WIDTH - 1}
            refY={ARROW_HEIGHT / 2}
            orient="auto-start-reverse"
            markerUnits="userSpaceOnUse"
            overflow="visible"
          >
            <polyline
              points={`1,2 ${ARROW_WIDTH},${ARROW_HEIGHT / 2} 1,${ARROW_HEIGHT - 2}`}
              fill="none"
              stroke={color}
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </marker>
        </defs>
        <path d={path} fill="none" stroke={color} strokeWidth={1.5} />
        {/* Make the hit area larger for easier interaction */}
        <path
          id={id}
          d={path}
          fill="none"
          stroke="transparent"
          strokeWidth={20}
          markerEnd={`url(#${markerId})`}
          onMouseEnter={showInsertButton}
          onMouseLeave={hideInsertButton}
        />
      </g>

      {!isEditingDisabled && !isPreview && (
        <EdgeLabelRenderer>
          {/* Mount the "+" only while the edge is hovered (the hit-area path
              above drives visibility) or the picker is open. Leaving it
              unmounted otherwise means an edge re-rendering every frame during a
              node's expand/collapse animation does no label work — only the SVG
              path updates. AnimatePresence fades/scales it in and out. */}
          <AnimatePresence initial={false}>
            {(isInsertButtonVisible || isInsertOpen) && (
              <motion.div
                key="insert-affordance"
                data-testid={`automations.editor.edge.${id}.insertZone`}
                className="nodrag nopan"
                onMouseEnter={showInsertButton}
                onMouseLeave={hideInsertButton}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                transition={{ duration: 0.12, ease: 'easeOut' }}
                style={{
                  position: 'absolute',
                  top: labelY,
                  left: labelX,
                  // Centre the button on the midpoint via margins; framer-motion
                  // owns the element transform (scale), so a translate here
                  // would be clobbered.
                  marginTop: -EDGE_INSERT_BUTTON_SIZE / 2,
                  marginLeft: -EDGE_INSERT_BUTTON_SIZE / 2,
                  // Sit above selected edges, which React Flow elevates to
                  // z-index 1000 (elevateEdgesOnSelect) — otherwise a focused
                  // edge's line covers the "+" and blocks clicking it.
                  zIndex: 1001,
                  pointerEvents: 'auto'
                }}
              >
                <EdgeInsertButton edgeId={id} onOpenChange={setIsInsertOpen} />
              </motion.div>
            )}
          </AnimatePresence>
        </EdgeLabelRenderer>
      )}
    </>
  )
}
